import { stripIgnoredCharacters, type ASTNode } from 'graphql'
import Router from 'next/router'
import { ApolloClient as ApolloClientBase, ApolloLink, HttpLink } from '@apollo/client'
import type { useApolloClient } from '@apollo/client/react'
import { ErrorLink } from '@apollo/client/link/error'
import { isEmpty } from 'lodash-es'
import { getAuthorizationHeaders } from 'lib/helpers/getAuthorizationHeaders/getAuthorizationHeaders'
import { deleteTokenCookie } from 'lib/helpers/deleteTokenCookie/deleteTokenCookie'
import { getDeviceTokenHeaders } from 'lib/helpers/getDeviceTokenHeaders/getDeviceTokenHeaders'
import { getGraphQLErrors } from 'lib/helpers/getGraphQLErrors/getGraphQLErrors'
import { isUnauthenticatedError } from 'lib/helpers/isUnauthenticatedError/isUnauthenticatedError'
import { isNetworkError } from 'lib/helpers/isNetworkError/isNetworkError'
import { isSSR } from 'lib/helpers/isSSR/isSSR'
import { getActiveTimeZone } from 'lib/hooks/useActiveTimeZone/useActiveTimeZone'
import { addAction } from 'lib/tracking/datadog'
import { getLoginPath } from 'lib/navigation/helpers/getLoginPath/getLoginPath'
import { getPostLoginNext } from 'lib/navigation/helpers/getPostLoginNext/getPostLoginNext'
import { emitter, emitterEvents } from 'lib/emitter'
import { qsStringSchema } from 'lib/zod'
import { t } from 'lib/translations/i18next'
import { RouterPage } from 'lib/navigation/consts'
import { trackMutationStatusLink } from 'lib/apollo-client/links/trackMutationStatusLink/trackMutationStatusLink'
import { trackQueryStatusLink } from 'lib/apollo-client/links/trackQueryStatusLink/trackQueryStatusLink'
import { retryLink } from 'lib/apollo-client/links/retryLink/retryLink'
import { pusherLink } from 'lib/apollo-client/links/pusherLink/pusherLink'
import { apolloDefaultOptions } from './apolloDefaultOptions'
import { getFeatureGatingError } from './helpers/getFeatureGatingError/getFeatureGatingError'
import { getOperationName } from './helpers/getOperationName/getOperationName'
import { getApolloCache } from './helpers/getApolloCache/getApolloCache'

let inlinedBusinessId: string | null = null

const httpLink = new HttpLink({
  uri: (operation) => `${process.env.BACKEND_APP_URL}/graphql?${getOperationName(operation)}`,
  print(ast: ASTNode, originalPrint: (ast: ASTNode) => string) {
    // Reduces query size by 25%
    return stripIgnoredCharacters(originalPrint(ast))
  },
})

export const errorHandler: ErrorLink.ErrorHandler = ({ error, operation }) => {
  // Counts what the RUM `beforeSend` filter drops: a request that never reached the server is not
  // an application fault, but its rate is the signal that separates one user's flaky connection
  // from an outage. Alert on this action, not on the error events.
  if (isNetworkError(error)) {
    addAction('graphql_transport_failure', { operation: operation.operationName })
  }

  const hasFeatureUpsellListeners = emitter.hasListeners(emitterEvents.featureUpsell)
  const featureGatingError = getFeatureGatingError(getGraphQLErrors(error))

  if (featureGatingError && hasFeatureUpsellListeners) {
    emitter.emit(emitterEvents.featureUpsell, featureGatingError)
  }

  if (!isUnauthenticatedError(error)) {
    return
  }

  // Counts what the RUM `beforeSend` filter drops, like `graphql_transport_failure` above: a
  // session-expiry spike is worth an alert even though no single 401 is an application fault.
  addAction('graphql_unauthenticated', { operation: operation.operationName })

  // Deliberately not deduplicated across the operations one expiry rejects: repeat `assign` calls to
  // the same URL collapse into one navigation, while a latch would strand a user who cancels an
  // `beforeunload` prompt — signed out, cookie gone, and every later 401 ignored.
  if (isSSR() || window.location.pathname === RouterPage.login.toString()) {
    return
  }

  // The cookie carries a 1-year expiry and nothing else clears it, so `isUserAuthenticated` — a
  // presence check — keeps reporting a rejected session as live and pre-auth pages keep firing
  // authenticated queries against it.
  deleteTokenCookie()

  window.location.assign(
    getLoginPath({
      warning: t('mfa::sessionExpired'),
      // `routeToLoginSetup` masks its query with an `as`, so the destination in flight is in the
      // router's state and not in the URL.
      next: getPostLoginNext({
        pathname: window.location.pathname,
        next: qsStringSchema.parse(Router.query.next),
      }),
    }),
  )
}

const errorLink = new ErrorLink(errorHandler)

interface RequestLinkParams {
  token?: Maybe<string>
  businessId?: Maybe<string>
}

export const requestLink = ({ token }: RequestLinkParams = {}) =>
  new ApolloLink((operation, forward) => {
    const contextHeaders: unknown = operation.getContext().headers
    const isHeaderObject = (value: unknown): value is Record<string, string> => {
      return typeof value === 'object' && value !== null
    }
    const headers: Record<string, string> = isHeaderObject(contextHeaders) ? contextHeaders : {}

    headers['X-Time-Zone'] = getActiveTimeZone()
    Object.assign(headers, getAuthorizationHeaders({ externalToken: token }))

    if (headers.Authorization && inlinedBusinessId) {
      headers['X-Business-ID'] = inlinedBusinessId
    }

    Object.assign(headers, getDeviceTokenHeaders(operation))

    if (!isEmpty(headers)) {
      operation.setContext({ headers })
    }

    return forward(operation)
  })

export const setRequestLinkBusinessId = (id: typeof inlinedBusinessId): void => {
  inlinedBusinessId = id
}

type InitializeApolloProps = RequestLinkParams
export const initializeApollo = ({ token, businessId }: InitializeApolloProps = {}) => {
  // `inlinedBusinessId` is module state shared by every request a warm server process handles, so an
  // explicitly-passed business — including `null` for "none" — has to overwrite it or one request's
  // business leaks into the next one's headers. Omitting the key leaves it untouched, for callers
  // that drive it through `setRequestLinkBusinessId` instead.
  if (businessId !== undefined) {
    setRequestLinkBusinessId(businessId || null)
  }

  const links: ApolloLink[] = [
    requestLink({ token }),
    ...(isSSR() ? [] : [pusherLink({ token })]),
    trackMutationStatusLink,
    trackQueryStatusLink,
    retryLink,
    errorLink,
    httpLink,
  ]

  const client = new ApolloClientBase({
    cache: getApolloCache(),
    devtools: { enabled: process.env.VERCEL_ENV !== 'production' },
    link: ApolloLink.from(links),
    defaultOptions: apolloDefaultOptions,
    // Apollo Client 4 defaults to stamping `extensions.clientLibrary` onto every request; the
    // server ignores it, so it is pure payload weight.
    enhancedClientAwareness: { transport: false },
  })

  return client
}
export type ApolloClient = ReturnType<typeof initializeApollo>
export type ApolloCache = ReturnType<typeof useApolloClient>['cache']
